import React, { useMemo, useState } from 'react';
import {
    Box, Flex, Text, HStack, VStack, Divider, Checkbox, Input, InputGroup, InputLeftElement, Select,
    Icon, Tooltip, Button, SimpleGrid, Collapse,
} from '@chakra-ui/react';
import { FaSearch, FaQuestionCircle, FaInbox, FaFilter, FaTimes } from 'react-icons/fa';
import LinhaVendaPagamento from './LinhaVendaPagamento';
import RodapePaginacao from './RodapePaginacao';
import { FILTRO_VAZIO, ORDENS, POR_PAGINA, filtrarVendas, filtroAtivo } from './filtros';
import { formatarCentavos } from '../formato';

// Lista de vendas da aba Pagamentos com busca, filtros, ordem, paginacao e selecao
// em massa (a selecao vale para todas as filtradas, nao so a pagina).
const ListaVendasPagamento = ({
    titulo, ajuda, vendas, podeGerenciar, selecionadas, onAlternar, onMarcar, acaoLinha, textoVazio,
    recolhivel = false, abertaInicial = true, idPrefixo,
}) => {
    const [filtro, setFiltro] = useState(FILTRO_VAZIO);
    const [pagina, setPagina] = useState(1);
    const [maisFiltros, setMaisFiltros] = useState(false);
    const [aberta, setAberta] = useState(abertaInicial);

    const filtradas = useMemo(() => filtrarVendas(vendas, filtro), [vendas, filtro]);
    const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
    const paginaAtual = Math.min(pagina, paginas);
    const visiveis = filtradas.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);

    const marcadas = filtradas.filter((v) => selecionadas.has(v.venda_id)).length;
    const todas = filtradas.length > 0 && marcadas === filtradas.length;
    const somaFiltradas = filtradas.reduce((s, v) => s + v.valor_centavos, 0);
    // Mudar o filtro volta para a primeira pagina.
    const mudarFiltro = (novo) => { setFiltro(novo); setPagina(1); };
    const set = (campo) => (e) => mudarFiltro({ ...filtro, [campo]: e.target.value });

    return (
        <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" boxShadow="sm" overflow="hidden" mb={5}>
            <Flex px={{ base: 3, md: 4 }} py={3} align="center" justify="space-between" gap={3} flexWrap="wrap" borderBottomWidth={aberta ? '1px' : 0} borderBottomColor="gray.100">
                <HStack spacing={2}>
                    <Text fontWeight="semibold" color="gray.800" fontSize="sm">{titulo} ({vendas.length})</Text>
                    {ajuda && (
                        <Tooltip label={ajuda} hasArrow fontSize="xs" maxW="320px">
                            <span tabIndex={0} aria-label={ajuda}><Icon as={FaQuestionCircle} color="gray.400" boxSize={3.5} cursor="help" /></span>
                        </Tooltip>
                    )}
                    {vendas.length > 0 && <Text fontSize="xs" color="gray.500">{formatarCentavos(vendas.reduce((s, v) => s + v.valor_centavos, 0))}</Text>}
                </HStack>
                {recolhivel && (
                    <Button size="xs" variant="ghost" onClick={() => setAberta(!aberta)} aria-expanded={aberta}>
                        {aberta ? 'Ocultar' : 'Mostrar'}
                    </Button>
                )}
            </Flex>

            <Collapse in={aberta} animateOpacity style={{ overflow: 'visible' }}>
                {vendas.length > 0 && (
                    <Box px={{ base: 3, md: 4 }} py={3} bg="gray.50" borderBottomWidth="1px" borderBottomColor="gray.100">
                        <Flex gap={2} flexWrap="wrap" align="center">
                            <InputGroup size="sm" flex={{ base: '1 1 100%', md: '1 1 260px' }} maxW={{ md: '320px' }}>
                                <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
                                <Input bg="white" borderRadius="md" placeholder="Venda, pedido ou cliente" value={filtro.busca} onChange={set('busca')} aria-label={`Buscar em ${titulo}`} />
                            </InputGroup>
                            <Select size="sm" bg="white" borderRadius="md" w={{ base: 'calc(50% - 4px)', md: '170px' }} value={filtro.ordem} onChange={set('ordem')} aria-label={`Ordenar ${titulo}`}>
                                {ORDENS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Select>
                            <Button size="sm" variant={maisFiltros || filtroAtivo({ ...filtro, busca: '' }) ? 'solid' : 'outline'} colorScheme={filtroAtivo({ ...filtro, busca: '' }) ? 'orange' : 'gray'} leftIcon={<Icon as={FaFilter} />} onClick={() => setMaisFiltros(!maisFiltros)} w={{ base: 'calc(50% - 4px)', md: 'auto' }} aria-expanded={maisFiltros}>
                                Filtros
                            </Button>
                            {filtroAtivo(filtro) && (
                                <Button size="sm" variant="ghost" leftIcon={<Icon as={FaTimes} />} onClick={() => mudarFiltro({ ...FILTRO_VAZIO, ordem: filtro.ordem })}>
                                    Limpar
                                </Button>
                            )}
                        </Flex>
                        <Collapse in={maisFiltros} animateOpacity>
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mt={2}>
                                <Select size="sm" bg="white" borderRadius="md" value={filtro.canal} onChange={set('canal')} aria-label="Canal">
                                    <option value="">Todos os canais</option>
                                    <option value="site">Site</option>
                                    <option value="ml">Mercado Livre</option>
                                    <option value="manual">Venda manual</option>
                                </Select>
                                <Select size="sm" bg="white" borderRadius="md" value={filtro.status} onChange={set('status')} aria-label="Status da venda">
                                    <option value="">Todas as vendas</option>
                                    <option value="concluida">Concluídas</option>
                                    <option value="nao_concluida">Não concluídas</option>
                                </Select>
                                <Input size="sm" bg="white" borderRadius="md" type="date" value={filtro.de} onChange={set('de')} aria-label="Vendas a partir de" title="Vendas a partir de" />
                                <Input size="sm" bg="white" borderRadius="md" type="date" value={filtro.ate} onChange={set('ate')} aria-label="Vendas até" title="Vendas até" />
                            </SimpleGrid>
                        </Collapse>
                        {filtroAtivo(filtro) && (
                            <Text fontSize="xs" color="gray.600" mt={2}>
                                {filtradas.length} de {vendas.length} venda(s) no filtro, somando {formatarCentavos(somaFiltradas)}
                            </Text>
                        )}
                    </Box>
                )}

                {podeGerenciar && filtradas.length > 0 && (
                    <Flex px={{ base: 3, md: 4 }} py={2} borderBottomWidth="1px" borderBottomColor="gray.100" align="center" gap={3}>
                        <Checkbox
                            colorScheme="orange"
                            size="sm"
                            isChecked={todas}
                            isIndeterminate={marcadas > 0 && !todas}
                            onChange={() => onMarcar(filtradas.map((v) => v.venda_id), !todas)}
                            id={`${idPrefixo}-todas`}
                        >
                            {todas ? 'Desmarcar' : 'Selecionar'} {filtroAtivo(filtro) ? `as ${filtradas.length} filtradas` : `todas (${filtradas.length})`}
                        </Checkbox>
                        {marcadas > 0 && <Text fontSize="xs" color="gray.500">{marcadas} selecionada(s) nesta lista</Text>}
                    </Flex>
                )}

                {vendas.length === 0 ? (
                    <Flex direction="column" align="center" py={8} color="gray.500">
                        <Icon as={FaInbox} boxSize={8} color="gray.300" mb={2} />
                        <Text fontSize="sm">{textoVazio}</Text>
                    </Flex>
                ) : filtradas.length === 0 ? (
                    <Flex direction="column" align="center" py={8} color="gray.500">
                        <Text fontSize="sm">Nenhuma venda neste filtro.</Text>
                        <Button size="xs" variant="link" colorScheme="orange" mt={1} onClick={() => mudarFiltro({ ...FILTRO_VAZIO, ordem: filtro.ordem })}>Limpar filtros</Button>
                    </Flex>
                ) : (
                    <VStack align="stretch" spacing={0} divider={<Divider />}>
                        {visiveis.map((v) => (
                            <LinhaVendaPagamento
                                key={v.venda_id}
                                venda={v}
                                selecionavel={podeGerenciar}
                                selecionada={selecionadas.has(v.venda_id)}
                                onSelecionar={onAlternar}
                                acao={podeGerenciar && acaoLinha ? acaoLinha(v) : null}
                            />
                        ))}
                    </VStack>
                )}
                <RodapePaginacao pagina={paginaAtual} paginas={paginas} total={filtradas.length} porPagina={POR_PAGINA} onMudar={setPagina} rotulo="vendas" />
            </Collapse>
        </Box>
    );
};

export default ListaVendasPagamento;
